import { assignmentLockHistoryItemType } from '@store/rtk/assignment';
import { formatUTCToLocal } from '@src/utils';

export type SortDirection = 'asc' | 'desc';
export type LockRecordKind =
  | 'lockedAccepting'
  | 'lockedNotAccepting'
  | 'unlocked';

export type AssignmentLockRow = {
  id: string;
  payorId: number;
  payer: string;
  lockStatus: LockRecordKind;
  desc: string;
  acc: number | string;
  add: number | string;
  wc: number | string;
  gur: number | string;
  bls: number | string;
  als: number | string;
  sct: number | string;
  fw: number | string;
  rw: number | string;
  total: number | string;
  demand: number | string;
  standing: number | string;
  dateTime: string;
  user: string;
  userRole: string;
  download: string;
  confirmWithPerson: string;
};

type AssignmentLockPayload =
  | Array<assignmentLockHistoryItemType>
  | {
      content?: Array<assignmentLockHistoryItemType>;
      rows?: Array<assignmentLockHistoryItemType>;
      data?: Array<assignmentLockHistoryItemType>;
    };

const getNestedValue = (item: Record<string, unknown>, path: string) => {
  return path.split('.').reduce<unknown>((value, key) => {
    if (value && typeof value === 'object' && key in value) {
      return (value as Record<string, unknown>)[key];
    }

    return undefined;
  }, item);
};

const valueFrom = (item: Record<string, unknown>, keys: Array<string>) =>
  keys
    .map((key) => getNestedValue(item, key))
    .find((value) => value !== undefined && value !== null) ?? '';

const toCount = (value: unknown) => {
  const numberValue = Number(value);

  return Number.isFinite(numberValue) ? numberValue : 0;
};

const parseLosSnapshot = (
  item: assignmentLockHistoryItemType
): Record<string, unknown> => {
  const snapshot = valueFrom(item, ['losSnapshot']);

  if (snapshot && typeof snapshot === 'object') {
    return snapshot as Record<string, unknown>;
  }

  if (typeof snapshot !== 'string') {
    return {};
  }

  try {
    const parsed = JSON.parse(snapshot);

    return parsed && typeof parsed === 'object'
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return Object.fromEntries(
      snapshot
        .split(/[;,|]/)
        .map((part) => part.trim().split(/[:=]/))
        .filter(([key, value]) => key && value !== undefined)
        .map(([key, value]) => [key.trim(), value.trim()])
    );
  }
};

export const getLockRecordKind = (
  item: assignmentLockHistoryItemType
): LockRecordKind => {
  const acceptedValue = valueFrom(item, [
    'accepted',
    'accepting',
    'isAccepting',
    'acceptingRides',
    'isAcceptingRides',
    'contractorAccepting',
  ]);
  const isAccepted =
    acceptedValue === true || String(acceptedValue).toLowerCase() === 'true';

  const actionValue = String(
    valueFrom(item, ['action', 'recordType', 'type', 'status', 'lockStatus'])
  ).toLowerCase();

  if (actionValue.includes('unlock')) {
    return 'unlocked';
  }

  if (
    actionValue.includes('not accepting') ||
    actionValue.includes('not_accepting') ||
    actionValue.includes('not-accepting')
  ) {
    return 'lockedNotAccepting';
  }

  if (actionValue.includes('accepting') || actionValue.includes('lock')) {
    return isAccepted ? 'lockedAccepting' : 'lockedNotAccepting';
  }

  const lockedValue = valueFrom(item, ['locked', 'isLocked']);

  if (lockedValue !== '') {
    if (
      lockedValue === false ||
      String(lockedValue).toLowerCase() === 'false'
    ) {
      return 'unlocked';
    }

    return isAccepted ? 'lockedAccepting' : 'lockedNotAccepting';
  }

  return isAccepted ? 'lockedAccepting' : 'lockedNotAccepting';
};

const losKeys = (key: string) => [
  key,
  key.toUpperCase(),
  key.toLowerCase(),
  `${key}Count`,
  `${key.toUpperCase()}Count`,
  `los.${key}`,
  `los.${key.toUpperCase()}`,
  `losCounts.${key}`,
  `losCounts.${key.toUpperCase()}`,
  `levelOfService.${key}`,
  `levelOfService.${key.toUpperCase()}`,
  `levelOfServiceCounts.${key}`,
  `levelOfServiceCounts.${key.toUpperCase()}`,
];

const snapshotOrItemValue = (
  snapshot: Record<string, unknown>,
  item: assignmentLockHistoryItemType,
  keys: Array<string>
) => {
  const snapshotValue = valueFrom(snapshot, keys);

  return snapshotValue !== '' ? snapshotValue : valueFrom(item, keys);
};

export const normalizeAssignmentLockRows = (
  rows: Array<assignmentLockHistoryItemType>
): Array<AssignmentLockRow> =>
  rows.map((item, index) => {
    const kind = getLockRecordKind(item);
    const losSnapshot = parseLosSnapshot(item);

    return {
      id: String(valueFrom(item, ['id', 'fileId']) || `lock-history-${index}`),
      payorId: Number(valueFrom(item, ['payorId', 'payerId'])),
      payer: String(valueFrom(item, ['payer', 'payor'])),
      lockStatus: kind,
      desc:
        kind === 'unlocked'
          ? ''
          : kind === 'lockedAccepting'
            ? 'Accepting'
            : 'Not Accepting',
      acc: toCount(snapshotOrItemValue(losSnapshot, item, losKeys('acc'))),
      add: toCount(snapshotOrItemValue(losSnapshot, item, losKeys('add'))),
      wc: toCount(snapshotOrItemValue(losSnapshot, item, losKeys('wc'))),
      gur: toCount(snapshotOrItemValue(losSnapshot, item, losKeys('gur'))),
      bls: toCount(snapshotOrItemValue(losSnapshot, item, losKeys('bls'))),
      als: toCount(snapshotOrItemValue(losSnapshot, item, losKeys('als'))),
      sct: toCount(snapshotOrItemValue(losSnapshot, item, losKeys('sct'))),
      fw: toCount(snapshotOrItemValue(losSnapshot, item, losKeys('fw'))),
      rw: toCount(snapshotOrItemValue(losSnapshot, item, losKeys('rw'))),
      total: toCount(snapshotOrItemValue(losSnapshot, item, ['totalCount'])),
      demand: toCount(snapshotOrItemValue(losSnapshot, item, ['demandCount'])),
      standing: toCount(
        snapshotOrItemValue(losSnapshot, item, ['standingCount'])
      ),
      dateTime: (() => {
        const rawDateTime = valueFrom(item, ['performedAt']);

        return rawDateTime ? formatUTCToLocal(String(rawDateTime)) : '';
      })(),
      user: String(valueFrom(item, ['performedByUserName'])),
      userRole: String(valueFrom(item, ['performedByUserRole'])),
      download: String(valueFrom(item, ['fileIdentifier'])),
      confirmWithPerson: String(valueFrom(item, ['confirmWithPerson'])),
    };
  });
